import { supabase } from '../lib/supabase';
import apiClient from './client';

export interface SlaPolicy {
  id: string;
  name: string;
  target_priority: string;
  first_response_hours: number;
  resolution_hours: number;
  is_active: boolean;
  organization_id?: string | null;
  created_at?: string;
}

const DEFAULT_SLA_POLICIES: SlaPolicy[] = [
  { id: 'sla-urgent', name: 'Critical / P1 SLA', target_priority: 'Urgent', first_response_hours: 1, resolution_hours: 4, is_active: true },
  { id: 'sla-high', name: 'High / P2 SLA', target_priority: 'High', first_response_hours: 2, resolution_hours: 8, is_active: true },
  { id: 'sla-medium', name: 'Medium / P3 SLA', target_priority: 'Medium', first_response_hours: 4, resolution_hours: 24, is_active: true },
  { id: 'sla-low', name: 'Low / P4 SLA', target_priority: 'Low', first_response_hours: 8, resolution_hours: 48, is_active: true }
];

export const slaApi = {
  getSlaPolicies: async (organizationId?: string): Promise<SlaPolicy[]> => {
    if (supabase) {
      try {
        let query = supabase
          .from('sla_policies')
          .select('*')
          .order('resolution_hours', { ascending: true });

        if (organizationId && organizationId !== 'ALL') {
          query = query.eq('organization_id', organizationId);
        }

        const { data, error } = await query;
        if (!error && data && data.length > 0) {
          return data as SlaPolicy[];
        }
      } catch {
        // Fall back to defaults
      }
      return DEFAULT_SLA_POLICIES;
    }

    try {
      const { data } = await apiClient.get<SlaPolicy[]>('/sla', {
        params: { organizationId }
      });
      return data;
    } catch {
      return DEFAULT_SLA_POLICIES;
    }
  },

  getSlaPolicyById: async (id: string): Promise<SlaPolicy | null> => {
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('sla_policies')
          .select('*')
          .eq('id', id)
          .maybeSingle();

        if (!error && data) return data as SlaPolicy;
      } catch {
        // Fall through
      }
      const found = DEFAULT_SLA_POLICIES.find(p => p.id === id);
      if (found) return found;
    }

    try {
      const { data } = await apiClient.get<SlaPolicy>(`/sla/${id}`);
      return data;
    } catch {
      return null;
    }
  },

  createSlaPolicy: async (payload: Partial<SlaPolicy>): Promise<SlaPolicy> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('sla_policies')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;
      return data as SlaPolicy;
    }

    const { data } = await apiClient.post<SlaPolicy>('/sla', payload);
    return data;
  },

  updateSlaPolicy: async (id: string, updates: Partial<SlaPolicy>): Promise<SlaPolicy> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('sla_policies')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as SlaPolicy;
    }

    const { data } = await apiClient.put<SlaPolicy>(`/sla/${id}`, updates);
    return data;
  },

  deleteSlaPolicy: async (id: string): Promise<{ success: boolean }> => {
    if (supabase) {
      const { error } = await supabase.from('sla_policies').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    }

    const { data } = await apiClient.delete<{ success: boolean }>(`/sla/${id}`);
    return data;
  }
};
